/*
  Samples page (sections/sbs-samples.liquid):
  - The grid is built from the page's data (data-samples-data), a batch at a time: the next batch loads once the end
    of the grid is on screen (or from the "Show more" button). Cards are made once from the data-card template and
    reused. Pictures are crops from Shopify's image server: the sample card in the grid and the viewer, the artwork
    in the small pictures (similar samples, the box).
  - Search by name (typos forgiven) or colour name; colour chips (any of the chosen); order by colour (the section's
    "Colour order") or A–Z. Rules in sbs-samples-logic.js (tested).
  - A card opens its sample in the viewer: beside the grid from 990px, in a sheet (<dialog>) below that. The viewer
    is one node, moved between the two when the width crosses 990px. On a sample's product page it opens on load.
  - Add / remove from the grid, the viewer, the similar samples and the box, without opening the cart drawer:
    POST /cart/add.js and /cart/change.js with ?skip=true (Unicorn's flag, kept so its drawer stays shut if the
    store goes back to it); their responses update the page (no second request), then sbs-cart:refresh redraws the
    SBS cart drawer (sections/sbs-cart.liquid) and the header count (or Unicorn.refreshCart() under Unicorn). While a request runs its buttons show it (a ring on the small ones, "Adding…" with a sweeping
    line on the viewer's) and the box already shows the change. Cart changes made in the drawer are picked up after
    a click in it, and on returning to the tab or the page.
  - The box (bottom bar) shows once a sample is in the cart: a place per redeemable sample, a message prompting up
    to the limit, and a button that opens the cart drawer (the cart page without one), closing an open sample
    sheet first.
  Without JS the page shows a plain list of links (noscript); a sample's page keeps its viewer and Add form.
*/

const PHONE = '(max-width: 989px)'
const CARD_WIDTHS = [200, 300, 400, 600]
const CARD_SIZES = '(min-width: 1400px) 210px, (min-width: 990px) 17vw, (min-width: 600px) 30vw, 46vw'
const VIEWER_WIDTHS = [400, 600, 800, 1100]
const THUMB_WIDTHS = [100, 200, 300]
const EAGER_CARDS = 8 // the first cards made load straight away; the rest when near the screen

if (!customElements.get('sbs-samples')) {
  customElements.define(
    'sbs-samples',
    class SbsSamples extends HTMLElement {
      async connectedCallback() {
        this.samples = JSON.parse(this.querySelector('[data-samples-data]').textContent)
        this.copy = JSON.parse(this.querySelector('[data-samples-copy]').textContent)
        this.byId = new Map(this.samples.map((s) => [String(s.id), s]))
        this.limit = Number(this.dataset.limit) || 3
        this.batch = Number(this.dataset.batch) || 24
        this.order = (this.dataset.colourOrder || '').split(',').map((c) => c.trim()).filter(Boolean)
        this.state = { query: '', colours: [], sort: 'colour' }
        this.list = []
        this.shown = 0
        this.cards = new Map() // variant id → card, made on first use
        this.inCart = new Map() // variant id → { key, id, quantity }
        this.busy = new Map() // variant id → 'add' | 'remove', while its request runs
        this.current = null
        this.root = window.Shopify?.routes?.root || '/'

        const $ = (selector) => this.querySelector(selector)
        this.el = {
          controls: $('[data-controls]'),
          search: $('[data-search]'),
          chips: $('[data-chips]'),
          count: $('[data-count]'),
          sortButtons: [...this.querySelectorAll('[data-sort]')],
          clearButtons: [...this.querySelectorAll('[data-clear-all]')],
          grid: $('[data-grid]'),
          more: $('[data-more]'),
          empty: $('[data-empty]'),
          emptyText: $('[data-empty-text]'),
          side: $('[data-side]'),
          viewer: $('[data-viewer]'),
          viewerEmpty: $('[data-viewer-empty]'),
          detail: $('[data-viewer-detail]'),
          img: $('[data-viewer-img]'),
          name: $('[data-viewer-name]'),
          meta: $('[data-viewer-meta]'),
          colours: $('[data-viewer-colours]'),
          viewerId: $('[data-viewer-id]'),
          viewerAdd: $('[data-viewer-add]'),
          status: $('[data-viewer-status]'),
          print: $('[data-viewer-print]'),
          similar: $('[data-viewer-similar]'),
          similarList: $('[data-viewer-similar-list]'),
          sheet: $('[data-sheet]'),
          box: $('[data-box]'),
          slots: $('[data-box-slots]'),
          boxTitle: $('[data-box-title]'),
          boxMessage: $('[data-box-message]'),
          announce: $('[data-announce]'),
          cardTemplate: $('template[data-card]')
        }
        // The plus, tick and close icons, for buttons made here.
        this.icons = {
          addButton: this.el.cardTemplate.content.querySelector('.sbs-samples__add').innerHTML,
          close: $('.sbs-samples-icon--close').outerHTML
        }

        try {
          this.logic = await import(this.dataset.logicSrc)
        } catch {
          return // a sample's page keeps its viewer and Add form; the grid stays empty
        }

        this.renderChips()
        this.bind()
        this.applyFilters()
        this.el.controls.hidden = false

        this.phone = window.matchMedia(PHONE)
        this.placeViewer()
        this.phone.addEventListener('change', () => this.placeViewer())
        this.followHeader()
        this.followBox()
        this.loadOnScroll()

        if (this.dataset.selected) this.show(this.dataset.selected, { openSheet: true, announce: false })
        this.refreshCart()
      }

      // ---------- Finding ----------

      renderChips() {
        this.el.chips.replaceChildren(
          ...this.logic.paletteOf(this.samples, this.order).map((c) => {
            const li = document.createElement('li')
            const button = document.createElement('button')
            button.type = 'button'
            button.className = 'sbs-samples__chip'
            button.dataset.colour = c.label
            button.setAttribute('aria-pressed', 'false')
            button.style.setProperty('--sbs-samples-chip', c.hex)
            button.innerHTML = '<span class="sbs-samples__chip-swatch" aria-hidden="true"></span><span></span><span class="sbs-samples__chip-count"></span>'
            button.children[1].textContent = c.label
            button.children[2].textContent = c.count
            li.append(button)
            return li
          })
        )
      }

      bind() {
        this.el.search.addEventListener('input', () => {
          this.state.query = this.el.search.value
          this.applyFilters()
        })
        this.el.search.addEventListener('keydown', (event) => {
          if (event.key !== 'Enter') return
          event.preventDefault()
          if (this.list.length) this.show(this.list[0].id, { openSheet: true })
        })

        this.addEventListener('click', (event) => this.onClick(event))
        this.el.sheet.addEventListener('click', (event) => {
          if (event.target === this.el.sheet) this.el.sheet.close() // the backdrop
        })
        this.el.sheet.addEventListener('close', () => {
          document.documentElement.classList.remove('sbs-samples-sheet-open')
          this.append(this.el.box) // back on the page
          if (this.scrollAfterClose) {
            // A colour chosen in the sheet: now the page can scroll again, up to the (shorter) results.
            this.scrollAfterClose = false
            this.scrollToResults('auto')
            this.el.chips.querySelector('[aria-pressed="true"]')?.focus({ preventScroll: true })
          } else if (this.returnFocus?.isConnected) {
            this.returnFocus.focus()
          }
        })
        // The box slides up the first time it shows, not each time it moves in or out of the sheet.
        this.el.box.addEventListener('animationend', () => this.el.box.classList.add('is-settled'), { once: true })

        // The info explanation: the button opens and closes it; a tap elsewhere or Escape (focus back on the button)
        // closes it.
        const toggle = this.querySelector('[data-explain-toggle]')
        const explain = this.querySelector('[data-explain-text]')
        if (toggle && explain) {
          const set = (open) => {
            explain.hidden = !open
            toggle.setAttribute('aria-expanded', String(open))
          }
          toggle.addEventListener('click', () => set(explain.hidden))
          document.addEventListener('click', (event) => {
            if (!explain.hidden && !toggle.contains(event.target) && !explain.contains(event.target)) set(false)
          })
          this.addEventListener('keydown', (event) => {
            if (event.key !== 'Escape' || explain.hidden) return
            set(false)
            toggle.focus()
          })
        }

        // Changes made in the cart drawer, and the cart as it is when coming back to the page. The SBS drawer sends
        // cart:updated after its changes (refreshOnly when it only redrew); Unicorn's is caught by a click in it.
        document.addEventListener('cart:updated', (event) => {
          if (event.detail?.refreshOnly) return
          clearTimeout(this.drawerTimer)
          this.drawerTimer = setTimeout(() => this.refreshCart(), 300)
        })
        document.addEventListener('click', (event) => {
          if (event.target.closest('#unicorn-root, #unicorn-cart-dialog')) {
            clearTimeout(this.drawerTimer)
            this.drawerTimer = setTimeout(() => this.refreshCart(), 1200)
          }
        })
        window.addEventListener('pageshow', (event) => event.persisted && this.refreshCart())
        document.addEventListener('visibilitychange', () => document.visibilityState === 'visible' && this.refreshCart())
      }

      onClick(event) {
        const t = event.target
        const open = t.closest('[data-sample-open]')
        if (open && !(event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button)) {
          event.preventDefault()
          this.show(open.closest('[data-sample-id]').dataset.sampleId, { openSheet: true })
          return
        }
        const add = t.closest('[data-sample-add]')
        if (add) {
          event.preventDefault()
          this.toggle(add.dataset.sampleAdd)
          return
        }
        if (t.closest('[data-viewer-add]')) {
          event.preventDefault()
          if (this.current) this.toggle(this.current)
          return
        }
        const show = t.closest('[data-show-id]')
        if (show) {
          this.show(show.dataset.showId, { openSheet: true })
          return
        }
        const chip = t.closest('.sbs-samples__chip')
        if (chip) {
          const label = chip.dataset.colour
          const on = this.state.colours.includes(label)
          this.state.colours = on ? this.state.colours.filter((c) => c !== label) : [...this.state.colours, label]
          this.applyFilters()
          return
        }
        const tag = t.closest('[data-filter-colour]')
        if (tag) {
          this.state.colours = [tag.dataset.filterColour]
          this.state.query = this.el.search.value = ''
          this.applyFilters()
          // Back up to the results. In the sheet, the page can't scroll until the sheet has closed (see 'close').
          if (this.el.sheet.open) {
            this.scrollAfterClose = true
            this.el.sheet.close()
          } else {
            this.scrollToResults(this.motion())
          }
          return
        }
        const sort = t.closest('[data-sort]')
        if (sort) {
          this.state.sort = sort.dataset.sort
          this.applyFilters()
          return
        }
        if (t.closest('[data-more-button]')) {
          const first = this.renderMore()
          first?.querySelector('[data-sample-open]').focus() // keyboard: carry on from the first new card
          return
        }
        if (t.closest('[data-clear-all]')) {
          this.state.colours = []
          this.state.query = this.el.search.value = ''
          this.applyFilters()
          // The button hides once cleared. From the keyboard (a click with no pointer: detail 0) focus goes to the
          // search; after a tap it isn't moved, so phones don't open their keyboard.
          if (event.detail === 0) this.el.search.focus()
          return
        }
        if (t.closest('[data-sheet-close]')) {
          this.el.sheet.close()
          return
        }
        const checkout = t.closest('[data-box-checkout]')
        if (checkout) {
          if (this.el.sheet.open) this.el.sheet.close() // the drawer would otherwise open behind the sheet
          if (document.querySelector('sbs-cart')) document.dispatchEvent(new CustomEvent('cart:open', { bubbles: true }))
          else if (window.Unicorn?.openCart) window.Unicorn.openCart()
          else window.location.href = checkout.dataset.cartUrl
        }
      }

      applyFilters() {
        this.list = this.logic.visibleSamples(this.samples, { ...this.state, order: this.order })
        this.shown = 0
        this.el.grid.replaceChildren()
        this.renderMore()

        const n = this.list.length
        this.el.count.textContent = this.logic.fillMessage(n === 1 ? this.copy.countOne : this.copy.countOther, { count: n })
        this.el.empty.hidden = n > 0
        if (!n) {
          const text = this.state.colours.length ? this.copy.noResultsColours : this.copy.noResults
          this.el.emptyText.textContent = this.logic.fillMessage(text, { query: this.state.query.trim() })
        }
        const filtered = Boolean(this.state.query.trim() || this.state.colours.length)
        this.el.clearButtons[0].hidden = !filtered
        for (const chip of this.el.chips.querySelectorAll('[data-colour]')) {
          chip.setAttribute('aria-pressed', String(this.state.colours.includes(chip.dataset.colour)))
        }
        for (const b of this.el.sortButtons) b.setAttribute('aria-pressed', String(b.dataset.sort === this.state.sort))
      }

      // ---------- Grid ----------

      card(id) {
        let card = this.cards.get(id)
        if (card) return card
        const s = this.byId.get(id)
        card = this.el.cardTemplate.content.firstElementChild.cloneNode(true)
        card.dataset.sampleId = id
        card.style.setProperty('--sbs-samples-card-ratio', s.card[3] / s.card[2]) // places the add button on the picture
        const link = card.querySelector('[data-sample-open]')
        link.href = s.url
        const img = card.querySelector('img')
        img.loading = this.cards.size < EAGER_CARDS ? 'eager' : 'lazy'
        img.decoding = 'async'
        img.width = 400
        img.height = Math.round((400 * s.card[3]) / s.card[2])
        img.sizes = CARD_SIZES
        img.srcset = this.logic.cropSrcset(s.img, s.card, CARD_WIDTHS)
        img.src = this.logic.cropUrl(s.img, s.card, 400)
        card.querySelector('.sbs-samples__name').textContent = s.name
        const add = card.querySelector('.sbs-samples__add')
        add.dataset.sampleAdd = id
        add.disabled = !s.available
        this.cards.set(id, card)
        return card
      }

      // Adds the next batch of the list to the grid; returns its first card.
      renderMore() {
        const next = this.list.slice(this.shown, this.shown + this.batch).map((s) => this.card(String(s.id)))
        this.el.grid.append(...next)
        this.shown += next.length
        this.el.more.hidden = this.shown >= this.list.length
        this.renderCartState()
        this.markCurrent()
        requestAnimationFrame(() => this.fillScreen())
        return next[0]
      }

      // The next batch loads once the end of the grid (the Show more button just after it) is on screen.
      loadOnScroll() {
        if (!('IntersectionObserver' in window)) return // the Show more button stays
        new IntersectionObserver((entries) => entries.some((e) => e.isIntersecting) && this.fillScreen()).observe(this.el.more)
      }

      // Also after each batch: on a screen taller than the batch, the end is still in view, so the next one follows.
      fillScreen() {
        if (this.el.more.hidden) return
        if (this.el.more.getBoundingClientRect().top < window.innerHeight) this.renderMore()
      }

      markCurrent() {
        for (const [id, card] of this.cards) {
          const link = card.querySelector('[data-sample-open]')
          if (id === this.current) link.setAttribute('aria-current', 'true')
          else link.removeAttribute('aria-current')
        }
      }

      // ---------- Viewer ----------

      show(id, { openSheet = false, announce = true } = {}) {
        const s = this.byId.get(String(id))
        if (!s) return
        this.current = String(id)
        this.markCurrent()

        this.el.viewerEmpty.hidden = true
        this.el.detail.hidden = false
        const img = this.el.img
        img.classList.add('is-loading')
        img.onload = () => img.classList.remove('is-loading')
        img.setAttribute('width', 800) // proportions only; the frame sets the size
        img.setAttribute('height', Math.round((800 * s.card[3]) / s.card[2]))
        img.srcset = this.logic.cropSrcset(s.img, s.card, VIEWER_WIDTHS)
        img.src = this.logic.cropUrl(s.img, s.card, 800)
        if (img.complete) img.classList.remove('is-loading')
        this.el.name.textContent = s.name
        this.el.status.textContent = '' // an error belongs to the sample it happened on
        this.el.meta.textContent = this.logic.fillMessage(this.copy.meta, { price: s.price })
        this.el.viewerId.value = s.id
        this.el.print.hidden = !s.print
        if (s.print) this.el.print.href = s.print

        this.el.colours.replaceChildren(
          ...s.colours.map((c) => {
            const li = document.createElement('li')
            const b = document.createElement('button')
            b.type = 'button'
            b.className = 'sbs-samples__tag'
            b.dataset.filterColour = c.label
            b.style.setProperty('--sbs-samples-chip', c.hex)
            b.setAttribute('aria-label', this.logic.fillMessage(this.copy.colourButton, { colour: c.label }))
            b.innerHTML = '<span class="sbs-samples__chip-swatch" aria-hidden="true"></span><span></span>'
            b.children[1].textContent = c.label
            li.append(b)
            return li
          })
        )

        this.renderSimilar()
        this.renderCartState()

        if (openSheet && this.phone.matches) this.openSheet()
        else if (this.el.sheet.open) this.el.viewer.scrollTo({ top: 0, behavior: this.motion() })
        if (announce) this.say(this.logic.fillMessage(this.copy.showing, { name: s.name }))
      }

      thumb(img, s) {
        img.decoding = 'async'
        img.srcset = this.logic.cropSrcset(s.img, s.art, THUMB_WIDTHS)
        img.src = this.logic.cropUrl(s.img, s.art, 200)
      }

      // Chosen samples go to the end of the similar ones here, when a sample is shown, not when one is added (an
      // added one keeps its place and shows its tick).
      renderSimilar() {
        const s = this.byId.get(this.current)
        if (!s) return
        const similar = this.logic.similarSamples(s, this.samples, { limit: 8, later: [...this.inCart.keys()].map(Number) })
        this.el.similar.hidden = !similar.length
        this.el.similarList.replaceChildren(
          ...similar.map((other) => {
            const li = document.createElement('li')
            li.className = 'sbs-samples__mini'
            li.innerHTML =
              '<button type="button" class="sbs-samples__mini-open"><span class="sbs-samples__frame"><img alt="" width="200" height="200" loading="lazy" sizes="(min-width: 990px) 90px, 30vw"></span><span class="sbs-samples__mini-name"></span></button>' +
              '<button type="button" class="sbs-samples__add sbs-samples__add--mini"></button>'
            const [open, add] = li.children
            open.dataset.showId = other.id
            this.thumb(open.querySelector('img'), other)
            open.querySelector('.sbs-samples__mini-name').textContent = other.name
            add.dataset.sampleAdd = other.id
            add.disabled = !other.available
            add.innerHTML = this.icons.addButton
            return li
          })
        )
      }

      placeViewer() {
        const into = this.phone.matches ? this.el.sheet : this.el.side
        if (this.el.viewer.parentElement !== into) into.append(this.el.viewer)
        if (!this.phone.matches && this.el.sheet.open) this.el.sheet.close()
      }

      // While the sheet is open everything outside it is blocked, so the box moves into it (along the bottom, over the
      // sample) and back out when it closes.
      openSheet() {
        if (!this.el.sheet.open) {
          this.returnFocus = document.activeElement
          document.documentElement.classList.add('sbs-samples-sheet-open')
          this.el.sheet.append(this.el.box)
          this.el.sheet.showModal()
        }
        this.el.viewer.scrollTop = 0
        this.el.name.focus({ preventScroll: true })
      }

      // ---------- Cart ----------

      async toggle(id) {
        id = String(id)
        if (this.busy.has(id)) return
        if (this.busy.size) this.overlapped = true // requests crossed: re-read the cart once they're all done
        const s = this.byId.get(id)
        const line = this.inCart.get(id)
        this.el.status.textContent = '' // an earlier error
        this.busy.set(id, line ? 'remove' : 'add')
        this.renderCartState()
        let failed = false
        try {
          if (line) {
            this.setCart(await this.post('cart/change.js', { id: line.key, quantity: 0 }))
          } else {
            const added = await this.post('cart/add.js', { items: [{ id: Number(id), quantity: 1 }] })
            for (const item of added.items || []) {
              this.inCart.set(String(item.variant_id), { key: item.key, id: item.variant_id, quantity: item.quantity })
            }
          }
        } catch {
          failed = true
        }
        this.busy.delete(id)
        this.renderCartState()
        if (failed) {
          this.say(this.copy.error)
          this.el.status.textContent = this.copy.error
        } else {
          const template = line ? this.copy.removed : this.copy.added
          this.say(this.logic.fillMessage(template, { name: s?.name || '', message: this.message() }))
        }
        if (!this.busy.size && this.overlapped) {
          this.overlapped = false
          this.refreshCart()
        }
        document.dispatchEvent(new CustomEvent('sbs-cart:refresh')) // the SBS cart drawer and the header count
        window.Unicorn?.refreshCart?.()?.catch?.(() => {}) // under Unicorn: its drawer catches up when it next opens anyway
      }

      async post(path, body) {
        const response = await fetch(`${this.root}${path}?skip=true`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body)
        })
        if (!response.ok) throw new Error(String(response.status))
        return response.json()
      }

      setCart(cart) {
        this.inCart = new Map(this.logic.samplesInCart(cart, this.dataset.productType).map((l) => [String(l.id), l]))
      }

      async refreshCart() {
        if (this.busy.size) return // a request is running; its response updates the page
        try {
          this.setCart(await fetch(`${this.root}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json()))
        } catch {
          return
        }
        this.renderCartState()
      }

      // Samples in the cart, counting adds and removals still running (the box shows them straight away).
      count() {
        let n = 0
        for (const line of this.inCart.values()) n += line.quantity
        for (const [id, kind] of this.busy) {
          if (kind === 'add' && !this.inCart.has(id)) n++
          if (kind === 'remove' && this.inCart.has(id)) n -= this.inCart.get(id).quantity
        }
        return n
      }

      message(count = this.count()) {
        const stage = this.logic.boxStage(count, this.limit)
        return this.logic.fillMessage(this.copy[stage], {
          count,
          limit: this.limit,
          remaining: Math.max(this.limit - count, 0)
        })
      }

      renderCartState() {
        const count = this.count()
        const message = this.message(count)

        for (const button of this.querySelectorAll('[data-sample-add]')) {
          const id = button.dataset.sampleAdd
          const s = this.byId.get(id)
          const busy = this.busy.get(id)
          const added = busy ? busy === 'add' : this.inCart.has(id) // shows where it's going while it runs
          button.classList.toggle('is-added', added)
          button.classList.toggle('is-busy', Boolean(busy))
          button.setAttribute('aria-busy', String(Boolean(busy)))
          if (s && !button.classList.contains('sbs-samples__slot-remove')) {
            button.setAttribute('aria-label', this.logic.fillMessage(added ? this.copy.cardRemove : this.copy.cardAdd, { name: s.name }))
          }
        }

        const s = this.current && this.byId.get(this.current)
        if (s) {
          const busy = this.busy.get(this.current)
          const added = this.inCart.has(this.current)
          const button = this.el.viewerAdd
          button.textContent = busy
            ? busy === 'add'
              ? this.copy.adding
              : this.copy.removing
            : !s.available && !added
              ? this.copy.soldOut
              : added
                ? this.copy.remove
                : this.copy.add
          button.disabled = !busy && !s.available && !added
          button.classList.toggle('is-busy', Boolean(busy))
          button.setAttribute('aria-busy', String(Boolean(busy)))
          button.classList.toggle('is-added', added && !busy)
        }

        this.renderBox(count, message)
      }

      renderBox(count, message) {
        const pendingAdds = [...this.busy].filter(([id, kind]) => kind === 'add' && !this.inCart.has(id)).map(([id]) => id)
        const ids = [...this.inCart.keys(), ...pendingAdds]
        this.el.box.hidden = ids.length === 0
        this.el.boxTitle.textContent = this.logic.fillMessage(count > this.limit ? this.copy.boxTitleOver : this.copy.boxTitle, {
          count,
          limit: this.limit
        })
        this.el.boxMessage.textContent = message

        const slots = []
        for (let i = 0; i < Math.max(this.limit, ids.length); i++) {
          const id = ids[i]
          const li = document.createElement('li')
          li.className = 'sbs-samples__slot'
          if (!id) {
            li.classList.add('sbs-samples__slot--empty')
            li.innerHTML = '<span class="visually-hidden"></span>'
            li.firstChild.textContent = this.copy.slotEmpty
            slots.push(li)
            continue
          }
          const sample = this.byId.get(id)
          li.classList.toggle('is-pending', this.busy.has(id))
          li.innerHTML =
            '<button type="button" class="sbs-samples__slot-open"><img alt="" width="100" height="100"></button>' +
            '<button type="button" class="sbs-samples__slot-remove"></button>'
          const [open, remove] = li.children
          if (sample) {
            open.dataset.showId = id
            open.setAttribute('aria-label', sample.name)
            this.thumb(open.firstChild, sample)
          } else {
            open.disabled = true
            open.firstChild.remove()
          }
          remove.dataset.sampleAdd = id
          remove.innerHTML = this.icons.close
          remove.setAttribute('aria-label', this.logic.fillMessage(this.copy.slotRemove, { name: sample?.name || '' }))
          remove.setAttribute('aria-busy', String(this.busy.has(id)))
          remove.classList.toggle('is-busy', this.busy.has(id))
          li.classList.toggle('is-extra', i >= this.limit) // phones: past the redeemable places, shown as "+N"
          slots.push(li)
        }
        const extra = ids.length - this.limit
        if (extra > 0) {
          const li = document.createElement('li')
          li.className = 'sbs-samples__slot sbs-samples__slot--more'
          li.innerHTML = '<span aria-hidden="true"></span><span class="visually-hidden"></span>'
          li.firstChild.textContent = `+${extra}`
          li.lastChild.textContent = this.logic.fillMessage(this.copy.slotMore, { count: extra })
          slots.push(li)
        }
        // Keep focus on a slot's button when the slots are redrawn under it.
        const focused = this.el.slots.contains(document.activeElement) ? document.activeElement : null
        const focusKey = focused && [focused.className, focused.dataset.sampleAdd || focused.dataset.showId]
        this.el.slots.replaceChildren(...slots)
        if (focusKey) {
          const again = [...this.el.slots.querySelectorAll('button')].find(
            (b) => b.className === focusKey[0] && (b.dataset.sampleAdd || b.dataset.showId) === focusKey[1]
          )
          ;(again || this.el.box.querySelector('[data-box-checkout]')).focus()
        }
      }

      // ---------- Page ----------

      // Sticky viewer below the sticky header: its height (it changes with the menu's lines) as --sbs-samples-top.
      followHeader() {
        const header = document.querySelector('.site-header[data-sticky="true"]')
        if (!header) return
        const set = () => this.style.setProperty('--sbs-samples-top', `${Math.round(header.getBoundingClientRect().height)}px`)
        set()
        new ResizeObserver(set).observe(header)
      }

      // The box's height on <html>: room under the grid so the box never covers the last row, a shorter sticky
      // viewer, and Klaviyo's "Subscribe" tab lifted above it (html.sbs-samples-has-box).
      followBox() {
        const html = document.documentElement
        const set = () => {
          const height = this.el.box.hidden ? 0 : Math.round(this.el.box.getBoundingClientRect().height)
          html.style.setProperty('--sbs-samples-box-height', `${height}px`)
          html.classList.toggle('sbs-samples-has-box', height > 0)
        }
        new ResizeObserver(set).observe(this.el.box)
      }

      // The filters at the top of the window, just below the sticky header, with the first results under them. On
      // phones the colour row scrolls sideways: the chosen colour is brought into it.
      scrollToResults(behavior) {
        const header = parseFloat(getComputedStyle(this).getPropertyValue('--sbs-samples-top')) || 0
        const top = this.el.controls.getBoundingClientRect().top + window.scrollY - header - 12
        window.scrollTo({ top: Math.max(0, top), behavior })
        const chip = this.el.chips.querySelector('[aria-pressed="true"]')
        if (chip) {
          const row = this.el.chips
          const offset = chip.getBoundingClientRect().left - row.getBoundingClientRect().left + row.scrollLeft
          row.scrollTo({ left: offset - (row.clientWidth - chip.offsetWidth) / 2, behavior }) // centred in the row
        }
      }

      motion() {
        return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
      }

      say(text) {
        this.el.announce.textContent = ''
        requestAnimationFrame(() => (this.el.announce.textContent = text))
      }
    }
  )
}
